import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, User, Menu, X, LayoutDashboard, LogOut, Heart, ChevronDown, FileText, Search, Package, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import axios from 'axios';
import PromoSlot from './PromoBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared across page navigations so the header doesn't refetch on every page
let navCache = null;

export function BeeLogo({ className = 'w-9 h-9' }) {
  return (
    <div className={`${className} rounded-xl bg-honey flex items-center justify-center shadow-sm`}>
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#1F1A14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <ellipse cx="12" cy="14" rx="5" ry="6" fill="#1F1A14" stroke="none" />
        <path d="M7.5 12.5h9M7.2 15.5h9.6" stroke="#F5B301" />
        <path d="M9.5 8.5C8 6 5 5.5 4 7.5s1.5 4 5 3M14.5 8.5c1.5-2.5 4.5-3 5.5-1s-1.5 4-5 3" />
      </svg>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pagesMenuOpen, setPagesMenuOpen] = useState(false);
  const [nav, setNav] = useState(navCache || { pages: [], categories: [], hasSale: false });
  const [query, setQuery] = useState(new URLSearchParams(location.search).get('q') || '');

  useEffect(() => {
    if (navCache) return;
    Promise.all([
      axios.get(`${API}/pages`).then(r => (r.data.pages || []).filter(p => p.show_in_nav)).catch(() => []),
      axios.get(`${API}/products/categories`).then(r => r.data.categories || []).catch(() => []),
      axios.get(`${API}/products`, { params: { sale: true, limit: 1 } }).then(r => r.data.total > 0).catch(() => false),
    ]).then(([pages, categories, hasSale]) => {
      navCache = { pages, categories, hasSale };
      setNav(navCache);
    });
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isAdmin = user?.role === 'admin';
  const handleLogout = async () => {
    await logout();
    navigate('/');
    setUserMenuOpen(false);
  };
  const submitSearch = (e) => {
    e.preventDefault();
    navigate(query.trim() ? `/?q=${encodeURIComponent(query.trim())}` : '/');
    setMobileOpen(false);
  };

  const searchBox = (
    <form onSubmit={submitSearch} className="relative w-full" role="search">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
      <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search for anything…" data-testid="nav-search-input"
        className="w-full bg-sand border-2 border-transparent rounded-full pl-11 pr-24 py-2.5 text-ink text-sm placeholder-ink/40 focus:outline-none focus:border-honey focus:bg-white transition-colors" />
      <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 bg-ink text-white text-sm font-bold px-4 py-1.5 rounded-full hover:bg-ink/85">Search</button>
    </form>
  );

  const Badge = ({ n, color = 'bg-honey text-ink' }) => n > 0 ? (
    <span className={`absolute -top-1.5 -right-2 ${color} text-[10px] font-black min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center`}>{n > 99 ? '99+' : n}</span>
  ) : null;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-ink/10">
      <PromoSlot placement="top_bar" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 sm:gap-8">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
          <BeeLogo />
          <span className="font-black text-lg sm:text-xl tracking-tight text-ink leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Market <span className="text-honey-600">@</span> <span className="hidden sm:inline">Buzzzillion</span>
          </span>
        </Link>

        <div className="hidden md:block flex-1 max-w-2xl">{searchBox}</div>

        <div className="flex items-center gap-4 sm:gap-5 ml-auto">
          {isAdmin && (
            <Link to="/admin" className="hidden lg:flex text-ink/70 hover:text-ink text-sm font-semibold items-center gap-1">
              <LayoutDashboard className="w-4 h-4" /> Admin
            </Link>
          )}
          <Link to="/wishlist" className="relative text-ink/70 hover:text-coral-600 transition-colors" title="Favorites" data-testid="wishlist-nav-icon">
            <Heart className="w-6 h-6" />
            <span data-testid="wishlist-badge"><Badge n={wishlistCount} color="bg-coral text-white" /></span>
          </Link>
          <Link to={user ? '/cart' : '/auth'} className="relative text-ink/70 hover:text-ink transition-colors" title="Cart" data-testid="cart-icon">
            <ShoppingCart className="w-6 h-6" />
            <Badge n={itemCount} />
          </Link>

          <div className="relative">
            <button onClick={() => setUserMenuOpen(!userMenuOpen)} onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
              className="flex items-center gap-2 text-ink/70 hover:text-ink" data-testid="user-menu-btn">
              <div className="w-9 h-9 rounded-full bg-sand flex items-center justify-center"><User className="w-5 h-5" /></div>
              <span className="hidden lg:block text-sm font-semibold max-w-[120px] truncate">{user ? `Hi, ${(user.name || '').split(' ')[0] || 'there'}` : 'Sign in'}</span>
            </button>
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 top-11 w-56 bg-white border border-ink/10 rounded-2xl shadow-xl overflow-hidden z-50">
                  {user ? (
                    <>
                      <div className="px-4 py-3 border-b border-ink/10">
                        <p className="text-ink text-sm font-bold truncate">{user.name}</p>
                        <p className="text-ink/55 text-xs truncate">{user.email}</p>
                      </div>
                      {isAdmin && (
                        <Link to="/admin" className="menu-item"><LayoutDashboard className="w-4 h-4" /> Admin Panel</Link>
                      )}
                      <Link to="/orders" className="menu-item"><Package className="w-4 h-4" /> My Orders</Link>
                      <Link to="/wishlist" className="menu-item"><Heart className="w-4 h-4" /> Favorites {wishlistCount > 0 && `(${wishlistCount})`}</Link>
                      <button onMouseDown={e => e.preventDefault()} onClick={handleLogout} data-testid="logout-btn"
                        className="menu-item menu-item-danger border-t border-ink/10">
                        <LogOut className="w-4 h-4" /> Log out
                      </button>
                    </>
                  ) : (
                    <div className="p-4 space-y-2">
                      <p className="text-ink/60 text-sm">Sign in to check out, track orders and save favorites.</p>
                      <Link to="/auth" className="btn-primary block text-center !py-2 text-sm" data-testid="login-link">Sign in / Register</Link>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-ink/70" aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Category strip */}
      <div className="hidden md:block border-t border-ink/5">
        <div className="max-w-7xl mx-auto px-6 h-11 flex items-center gap-6 text-sm overflow-x-auto scroll-rail">
          <Link to="/" className="font-semibold text-ink/80 hover:text-ink whitespace-nowrap">All</Link>
          {nav.hasSale && (
            <Link to="/sale" className="font-black text-coral-600 hover:text-coral-700 whitespace-nowrap flex items-center gap-1" data-testid="nav-sale">
              <Tag className="w-3.5 h-3.5" /> Sale
            </Link>
          )}
          {nav.categories.slice(0, 10).map(c => (
            <Link key={c} to={`/?category=${encodeURIComponent(c)}`} className="text-ink/70 hover:text-ink whitespace-nowrap">{c}</Link>
          ))}
          {nav.pages.length > 0 && (
            <div className="relative ml-auto">
              <button onClick={() => setPagesMenuOpen(v => !v)} onBlur={() => setTimeout(() => setPagesMenuOpen(false), 150)}
                className="flex items-center gap-1 text-ink/70 hover:text-ink whitespace-nowrap" data-testid="nav-pages-btn">
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${pagesMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {pagesMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 top-8 min-w-[200px] bg-white border border-ink/10 rounded-xl shadow-xl overflow-hidden z-50">
                    {nav.pages.map(p => (
                      <Link key={p.id} to={`/pages/${p.slug}`} data-testid={`nav-page-link-${p.slug}`}
                        className="menu-item">
                        <FileText className="w-3.5 h-3.5 text-honey-700" /> {p.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-ink/10 bg-white px-4 py-4 space-y-1 overflow-hidden">
            <div className="pb-3">{searchBox}</div>
            <Link to="/" className="block text-ink/80 py-2 font-semibold">Shop all</Link>
            {nav.hasSale && <Link to="/sale" className="block text-coral-600 font-black py-2">Sale</Link>}
            {nav.categories.map(c => <Link key={c} to={`/?category=${encodeURIComponent(c)}`} className="block text-ink/70 py-2">{c}</Link>)}
            {nav.pages.map(p => <Link key={p.id} to={`/pages/${p.slug}`} className="block text-ink/70 py-2">{p.title}</Link>)}
            <div className="border-t border-ink/10 pt-2 mt-2">
              <Link to="/wishlist" className="block text-ink/70 py-2">Favorites {wishlistCount > 0 && `(${wishlistCount})`}</Link>
              {user && <Link to="/orders" className="block text-ink/70 py-2">My Orders</Link>}
              {isAdmin && <Link to="/admin" className="block text-honey-700 font-semibold py-2">Admin Panel</Link>}
              {!user && <Link to="/auth" className="block text-ink font-semibold py-2">Sign in</Link>}
              {user && <button onClick={handleLogout} className="block text-red-600 py-2 w-full text-left">Log out</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
